'use client';
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useImperativeHandle,
    forwardRef,
    ReactNode,
    ElementType,
    CSSProperties
  } from 'react';
  
  export interface InfiniteScrollProps {
    children: ReactNode;
    element?: ElementType;
    hasMore?: boolean;
    initialLoad?: boolean;
    isReverse?: boolean;
    loader?: ReactNode;
    loadMore: (page: number) => void;
    pageStart?: number;
    getScrollParent?: () => HTMLElement | null;
    threshold?: number;
    useCapture?: boolean;
    useWindow?: boolean;
    style?: CSSProperties;
    className?: string;
    id?: string;
  }
  
  export interface InfiniteScrollHandle {
    setDefaultLoader: (loader: ReactNode) => void;
    detachScrollListener: () => void;
    attachScrollListener: () => void;
  }
  
  
  const InfiniteScroll = forwardRef<InfiniteScrollHandle, InfiniteScrollProps>(
    (props, ref) => {
      const {
        children,
        element: Element = 'div',
        hasMore = false,
        initialLoad = true,
        isReverse = false,
        loader = null,
        loadMore,
        pageStart = 0,
        getScrollParent = null,
        threshold = 250,
        useCapture = false,
        useWindow = true,
        style,
        className,
        id,
        ...restProps
      } = props;
  
      const [pageLoaded, setPageLoaded] = useState<number>(pageStart);
      const [loadMoreTriggered, setLoadMoreTriggered] = useState<boolean>(false);
      const [beforeScrollHeight, setBeforeScrollHeight] = useState<number>(0);
      const [beforeScrollTop, setBeforeScrollTop] = useState<number>(0);
      
      const scrollComponentRef = useRef<HTMLElement>(null);
      const defaultLoaderRef = useRef<ReactNode>(null);
      const optionsRef = useRef<AddEventListenerOptions | boolean>(false);
      const passiveSupportedRef = useRef<boolean | null>(null);
      const initialLoadFiredRef = useRef<boolean>(false);
  
      // Check passive event listener support
      const isPassiveSupported = useCallback((): boolean => {
        if (passiveSupportedRef.current !== null) {
          return passiveSupportedRef.current;
        }
  
        let passive = false;
        const testOptions: any = {
          get passive() {
            passive = true;
            return true;
          }
        };
  
        try {
          window.addEventListener('test', () => {}, testOptions);
          window.removeEventListener('test', () => {}, testOptions);
        } catch (e) {
          console.error(e)
        }
        
        passiveSupportedRef.current = passive;
        return passive;
      }, []);
  
      // Get event listener options
      const getEventListenerOptions = useCallback((): AddEventListenerOptions | boolean => {
        if (optionsRef.current) {
          return optionsRef.current;
        }
  
        let options: AddEventListenerOptions | boolean = useCapture;
        if (isPassiveSupported()) {
          options = {
            capture: useCapture,
            passive: true
          };
        } else {
          options = {
            passive: false
          } as AddEventListenerOptions;
        }
        
        optionsRef.current = options;
        return options;
      }, [useCapture, isPassiveSupported]);
  
      // Get parent element
      const getParentElement = useCallback((el: HTMLElement | null): HTMLElement | null => {
        if (!el) return null;
        
        const scrollParent = getScrollParent && getScrollParent();
        if (scrollParent != null) {
          return scrollParent;
        }
        return el.parentElement;
      }, [getScrollParent]);
  
      // Calculate top position
      const calculateTopPosition = useCallback((el: HTMLElement | null): number => {
        if (!el) return 0;
        let offsetTop = 0;
        let current: HTMLElement | null = el;
        
        while (current) {
          offsetTop += current.offsetTop;
          current = current.offsetParent as HTMLElement | null;
        }
        
        return offsetTop;
      }, []);
  
      // Calculate offset
      const calculateOffset = useCallback((el: HTMLElement | null, scrollTop: number): number => {
        if (!el) return 0;
        return (
          calculateTopPosition(el) +
          (el.offsetHeight - scrollTop - window.innerHeight)
        );
      }, [calculateTopPosition]);
  
      // Mousewheel listener
      const mousewheelListener: any = useCallback((e: WheelEvent) => {
        if (e.deltaY === 1 && !isPassiveSupported()) {
          e.preventDefault();
        }
      }, [isPassiveSupported]);
  
      // Scroll listener
      const scrollListener = useCallback(() => {
        const el = scrollComponentRef.current;
        if (!el) return;
  
        const parentNode = getParentElement(el);
        if (!parentNode) return;
  
        let offset: number;
        if (useWindow) {
          const doc = document.documentElement || document.body.parentNode || document.body;
          const scrollTop = window.pageYOffset !== undefined
            ? window.pageYOffset
            : doc.scrollTop;
          
          if (isReverse) {
            offset = scrollTop;
          } else {
            offset = calculateOffset(el, scrollTop);
          }
        } else if (isReverse) {
          offset = parentNode.scrollTop;
        } else {
          offset = el.scrollHeight - parentNode.scrollTop - parentNode.clientHeight;
        }
  
        if (offset < Number(threshold) && (el && el.offsetParent !== null)) {
          detachScrollListener();
          setBeforeScrollHeight(parentNode.scrollHeight);
          setBeforeScrollTop(parentNode.scrollTop);
          
          if (typeof loadMore === 'function') {
            const nextPage = pageLoaded + 1;
            setPageLoaded(nextPage);
            loadMore(nextPage);
            setLoadMoreTriggered(true);
          }
        }
      }, [
        getParentElement,
        useWindow,
        isReverse,
        calculateOffset,
        threshold,
        loadMore,
        pageLoaded
      ]);
  
      // Detach mousewheel listener
      const detachMousewheelListener = useCallback(() => {
        let scrollEl: Window | HTMLElement = window;
        if (useWindow === false && scrollComponentRef.current) {
          scrollEl = scrollComponentRef.current.parentNode as HTMLElement;
        }
  
        const options = getEventListenerOptions();
        scrollEl.removeEventListener('wheel', mousewheelListener, options);
      }, [useWindow, getEventListenerOptions, mousewheelListener]);
  
      // Detach scroll listener
      const detachScrollListener = useCallback(() => {
        let scrollEl: Window | HTMLElement = window;
        if (useWindow === false && scrollComponentRef.current) {
          const parent = getParentElement(scrollComponentRef.current);
          if (parent) {
            scrollEl = parent;
          }
        }
  
        const options = getEventListenerOptions();
        if (typeof options === 'object') {
          scrollEl.removeEventListener('scroll', scrollListener, options);
          scrollEl.removeEventListener('resize', scrollListener, options);
        } else {
          scrollEl.removeEventListener('scroll', scrollListener, options);
          scrollEl.removeEventListener('resize', scrollListener, options);
        }
      }, [useWindow, getParentElement, getEventListenerOptions, scrollListener]);
  
      // Attach scroll listener
      const attachScrollListener = useCallback(() => {
        if (!scrollComponentRef.current) return;
  
        const parentElement = getParentElement(scrollComponentRef.current);
        if (!hasMore || !parentElement) return;
  
        let scrollEl: Window | HTMLElement = window;
        if (useWindow === false) {
          scrollEl = parentElement;
        }
  
        const options = getEventListenerOptions();
        
        scrollEl.addEventListener('wheel', mousewheelListener, options);
        scrollEl.addEventListener('scroll', scrollListener, options);
        scrollEl.addEventListener('resize', scrollListener, options);

        // Only fire the initial-load check once. Re-attaching on every
        // hasMore/loadMore identity change (see the effect below) would
        // otherwise re-trigger this synchronous check every time, and if
        // the offset is still under `threshold`, loadMore() fires again
        // before the previous call resolves — a runaway request loop.
        if (initialLoad && !initialLoadFiredRef.current) {
          initialLoadFiredRef.current = true;
          scrollListener();
        }
      }, [
        getParentElement,
        hasMore,
        useWindow,
        getEventListenerOptions,
        mousewheelListener,
        scrollListener,
        initialLoad
      ]);
  
      // Set default loader
      const setDefaultLoader = useCallback((loader: ReactNode) => {
        defaultLoaderRef.current = loader;
      }, []);
  
      // Expose methods via ref
      useImperativeHandle(ref, () => ({
        setDefaultLoader,
        detachScrollListener,
        attachScrollListener
      }));
  
      // ComponentDidMount equivalent
      useEffect(() => {
        setPageLoaded(pageStart);
        attachScrollListener();
  
        return () => {
          detachScrollListener();
          detachMousewheelListener();
        };
      }, [pageStart, attachScrollListener, detachScrollListener, detachMousewheelListener]);
  
      // ComponentDidUpdate equivalent for reverse scrolling
      useEffect(() => {
        if (isReverse && loadMoreTriggered && scrollComponentRef.current) {
          const parentElement = getParentElement(scrollComponentRef.current);
          if (parentElement) {
            parentElement.scrollTop =
              parentElement.scrollHeight -
              beforeScrollHeight +
              beforeScrollTop;
            setLoadMoreTriggered(false);
          }
        }
      }, [isReverse, loadMoreTriggered, beforeScrollHeight, beforeScrollTop, getParentElement]);
  
      // Re-attach scroll listener when dependencies change
      useEffect(() => {
        detachScrollListener();
        attachScrollListener();
      }, [hasMore, attachScrollListener, detachScrollListener]);
  
      // Create children array with loader
      const childrenArray = React.Children.toArray(children);
      if (hasMore) {
        const loaderElement: any = loader || defaultLoaderRef.current;
        if (loaderElement) {
          // React.Children.toArray already keys the elements above; this
          // element is appended after the fact, so it needs its own key
          // or React warns about a keyless child in the list.
          const keyedLoaderElement = React.createElement(
            React.Fragment,
            { key: '__infinite_scroll_loader__' },
            loaderElement,
          );
          if (isReverse) {
            childrenArray.unshift(keyedLoaderElement);
          } else {
            childrenArray.push(keyedLoaderElement);
          }
        }
      }
  
      // Handle ref assignment
      const handleRef = useCallback((node: HTMLElement | null) => {
        if (node) {
          scrollComponentRef.current = node;
        }
      }, []);
  
      // Create element props
      const elementProps = {
        ...restProps,
        ref: handleRef,
        style,
        className,
        id
      };
  
      return React.createElement(Element, elementProps, childrenArray);
    }
  );
  
  InfiniteScroll.displayName = 'InfiniteScroll';
  
  export default InfiniteScroll;